import { useParams } from "@tanstack/react-router";
import type { TabId } from "@/api/types";
import { GlobalTab } from "@/components/tabs-content/GlobalTab";
import { TeamsTab } from "@/components/tabs-content/TeamsTab";
import { StreaksTab } from "@/components/tabs-content/StreaksTab";
import { MoversTab } from "@/components/tabs-content/MoversTab";
import { EfficiencyTab } from "@/components/tabs-content/EfficiencyTab";
import { BurnTab } from "@/components/tabs-content/BurnTab";
import { AchievementsTab } from "@/components/tabs-content/AchievementsTab";
import { PulseTab } from "@/components/tabs-content/PulseTab";
import { EmptyState } from "@/components/tabs-content/shared";

const KNOWN_TABS: readonly TabId[] = [
  "global",
  "teams",
  "streaks",
  "movers",
  "efficiency",
  "burn",
  "achievements",
  "pulse",
] as const;

function isTabId(v: string): v is TabId {
  return (KNOWN_TABS as readonly string[]).includes(v);
}

function renderTab(tabId: TabId) {
  switch (tabId) {
    case "global":
      return <GlobalTab />;
    case "teams":
      return <TeamsTab />;
    case "streaks":
      return <StreaksTab />;
    case "movers":
      return <MoversTab />;
    case "efficiency":
      return <EfficiencyTab />;
    case "burn":
      return <BurnTab />;
    case "achievements":
      return <AchievementsTab />;
    case "pulse":
      return <PulseTab />;
  }
}

/** Dispatcher — picks the correct tab body based on the :tabId param.
 * Unknown ids render an empty state (still within the Shell).
 *
 * The wrapper element carries a `key` and a `data-tab-id` derived from
 * the route param so <AnimatePresence> (mounted in Shell) reliably
 * detects the swap when navigating across tabs. The key is redundant
 * with the route pathname used by Shell, but it makes the intent
 * explicit and also guarantees tab-body state (queries, filters in
 * local state) resets on tab change. */
export function TabPage() {
  const { tabId } = useParams({ strict: false }) as { tabId?: string };

  if (!tabId || !isTabId(tabId)) {
    return (
      <div key="tab-not-found" data-tab-id="__unknown__">
        <EmptyState
          title="Sección no encontrada."
          description="Usa la navegación de arriba para elegir un tab."
        />
      </div>
    );
  }

  return (
    <div key={tabId} data-tab-id={tabId}>
      {renderTab(tabId)}
    </div>
  );
}
